import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { onboardingApi } from '../../utils/onboardingApi';
import { useGetStarted } from './GetStartedContext';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useToast } from '../ui';
import { BillingForm, FamilyForm, CoachForm, ShareForm, EmailForm } from './StepForms';
import './GetStarted.css';

const AUTO_OPEN_KEY = 'getStartedAutoOpened';
const HOME_PATH = '/dashboard';
const RING = 2 * Math.PI * 16;

const lower = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export default function GetStarted() {
  const { user } = useAuth();
  const location = useLocation();
  const clubId = user?.clubId || user?.club?.id;

  const ctx = useGetStarted();
  const data = ctx?.data ?? null;
  const load = ctx?.refresh;
  const [open, setOpen] = useState(false);
  const [openStep, setOpenStep] = useState(null);
  const [pulse, setPulse] = useState(false);
  const toast = useToast();
  const lastDone = useRef(null); // done keys last seen; null before the first

  // A newly done step gives the pulse and the toast
  useEffect(() => {
    if (!data) return;
    const steps = data.steps || [];
    const doneKeys = steps.filter((s) => s.done).map((s) => s.key);
    if (lastDone.current) {
      const fresh = steps.filter((s) => s.done && !lastDone.current.includes(s.key));
      if (fresh.length) {
        const nextStep = steps.find((s) => s.key === data.next);
        setPulse(true);
        toast(`Done: ${fresh[0].title}.${nextStep ? ` Next: ${lower(nextStep.title)}.` : ''}`);
      }
    }
    lastDone.current = doneKeys;
  }, [data, toast]);

  // Opens by itself on the home page until the required steps are done, once per session
  useEffect(() => {
    if (!data?.show || location.pathname !== HOME_PATH) return;
    const requiredDone = data.steps.filter((s) => s.group === 'required').every((s) => s.done);
    const allSet = data.next === null && data.completedAt;
    if (requiredDone && !allSet) return;
    let seen = false;
    try { seen = sessionStorage.getItem(AUTO_OPEN_KEY) === '1'; } catch {}
    if (seen) return;
    try { sessionStorage.setItem(AUTO_OPEN_KEY, '1'); } catch {}
    setOpen(true);
  }, [data, location.pathname]);

  const show = !!data?.show;
  useEffect(() => {
    if (!show) return undefined;
    document.body.classList.add('has-get-started');
    return () => document.body.classList.remove('has-get-started');
  }, [show]);

  if (!show) return null;

  const steps = data.steps;
  const total = steps.length;
  const finished = (s) => s.done || s.skipped;
  const doneCount = steps.filter(finished).length;
  const nextStep = steps.find((s) => s.key === data.next) || steps.find((s) => !finished(s));
  const allSet = !nextStep && !!data.completedAt;
  const current = steps.find((s) => s.key === openStep && !finished(s)) || nextStep;

  const skip = async (key) => {
    try { await onboardingApi.skip(key); } catch {}
    load();
  };

  const form = (s) => {
    switch (s.key) {
      case 'billing': return <BillingForm gymnasts={data.gymnasts} billing={data.billing} />;
      case 'family': return <FamilyForm clubId={clubId} onChanged={load} />;
      case 'coach': return <CoachForm onChanged={load} />;
      case 'share': return <ShareForm joinUrl={data.club?.joinUrl} onChanged={load} />;
      case 'email': return <EmailForm club={data.club} clubId={clubId} />;
      default: return null;
    }
  };

  const renderStep = (s) => {
    const isOpen = current?.key === s.key && !finished(s);
    const cls = ['get-started__step', s.done && 'is-done', s.skipped && 'is-skipped', isOpen && 'is-open'].filter(Boolean).join(' ');
    const tag = s.done ? 'Done' : s.skipped ? 'Not needed' : '';
    return (
      <div className={cls} key={s.key} data-testid={`step-${s.key}`}>
        <span className="get-started__tick" aria-hidden="true">{s.done && <CheckIcon />}</span>
        <div className="get-started__step-main">
          <button type="button" className="get-started__step-title" aria-expanded={isOpen} onClick={() => setOpenStep(s.key)}>
            <b>{s.title}</b><span className="get-started__tag">{tag}</span>
          </button>
          {isOpen && (
            <div className="get-started__step-body">
              {s.help && <p className="get-started__muted">{s.help}</p>}
              {s.link
                ? <div className="get-started__actions"><Link className="bk-btn bk-btn--primary" to={s.link} onClick={() => setOpen(false)}>Go</Link></div>
                : form(s)}
              {s.group === 'recommended' && (
                <div className="get-started__actions">
                  <button type="button" className="bk-btn bk-btn--sm" onClick={() => skip(s.key)}>Not needed</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const group = (name) => steps.filter((s) => s.group === name).map(renderStep);

  return (
    <>
      {open && (
        <div className="get-started__sheet" role="dialog" aria-label="Get started checklist">
          {allSet ? (
            <div className="get-started__head">
              <div className="get-started__row"><h2>You're all set</h2><button type="button" className="bk-btn bk-btn--sm" onClick={() => setOpen(false)}>Close</button></div>
              <p className="get-started__muted">Every step is done. This list won't appear again.</p>
            </div>
          ) : (
            <>
              <div className="get-started__head">
                <div className="get-started__row">
                  <h2>Get your club started</h2>
                  <button type="button" className="bk-btn bk-btn--sm" onClick={() => setOpen(false)}>Minimise</button>
                </div>
                <div className="get-started__bar" aria-hidden="true"><i style={{ width: `${(doneCount / total) * 100}%` }} /></div>
                <p className="get-started__small">{doneCount} of {total} done. This stays here until every step is done or marked not needed.</p>
              </div>
              <div className="get-started__group">
                <span className="get-started__label">Needed to run the club</span>
                {group('required')}
              </div>
              <div className="get-started__group">
                <span className="get-started__label">Recommended</span>
                {group('recommended')}
              </div>
            </>
          )}
        </div>
      )}
      <button
        type="button"
        className={`get-started__fab${pulse ? ' is-pulsing' : ''}`}
        aria-expanded={open}
        onClick={() => { setOpen((o) => !o); setPulse(false); }}
        onAnimationEnd={() => setPulse(false)}
      >
        <svg viewBox="0 0 40 40" aria-hidden="true">
          <circle cx="20" cy="20" r="16" fill="none" strokeWidth="4" className="get-started__ring-bg" />
          <circle
            cx="20" cy="20" r="16" fill="none" strokeWidth="4" className="get-started__ring"
            strokeDasharray={RING} strokeDashoffset={RING * (1 - doneCount / total)}
            transform="rotate(-90 20 20)" strokeLinecap="round"
          />
          <text x="20" y="24" textAnchor="middle" className="get-started__count">{doneCount}/{total}</text>
        </svg>
        <span>
          {allSet ? "You're all set" : 'Get started'}
          {nextStep && <small>Next: {lower(nextStep.title)}</small>}
        </span>
      </button>
    </>
  );
}
